import type { FC, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useRawInitData,
  useTelegramUser,
  retrieveLaunchParams,
  isTelegramDark,
} from '@/shared/telegram';
import { authApi, httpClient } from '@/shared/api';
import { setTelemetryUser, clearTelemetryUser, setTelemetrySession } from '@/shared/observability';

interface AuthContextValue {
  isAuthenticated: boolean;
  authError: string | null;
  authenticate: () => Promise<string>;
  rawInitData: string | undefined;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const rawInitData = useRawInitData();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState<string | null>(
    rawInitData ? null : 'Telegram did not provide sign-in data. Reopen Shanti from the bot.'
  );
  const authPromiseRef = useRef<Promise<string> | null>(null);
  const tgUser = useTelegramUser();

  const authenticate = useCallback(async () => {
    // if authentication is already in progress, return the existing promise
    if (authPromiseRef.current) {
      return authPromiseRef.current;
    }

    const authPromise = Promise.resolve().then(async () => {
      try {
        if (!rawInitData) {
          const error = 'No Telegram init data';
          setAuthError(error);
          throw new Error(error);
        }

        const response = await authApi.exchangeTelegramAuth(rawInitData);
        httpClient.setToken(response.accessToken);
        setAuthError(null);
        setIsAuthenticated(true);
        return response.accessToken;
      } catch (error) {
        httpClient.setToken(null);
        setIsAuthenticated(false);
        setAuthError(error instanceof Error ? error.message : 'Authentication failed');
        throw error;
      } finally {
        authPromiseRef.current = null;
      }
    });

    authPromiseRef.current = authPromise;
    return authPromise;
  }, [rawInitData]);

  useEffect(() => {
    if (!isAuthenticated && !authError && !authPromiseRef.current && rawInitData) {
      void authenticate().catch(() => undefined);
    }
  }, [isAuthenticated, authError, rawInitData, authenticate]);

  useEffect(() => {
    httpClient.setUnauthorizedHandler(authenticate);
  }, [authenticate]);

  useEffect(() => {
    if (isAuthenticated && tgUser) {
      setTelemetryUser({ id: tgUser.id, username: tgUser.username });
      try {
        const lp = retrieveLaunchParams();
        setTelemetrySession({
          platform: String(lp.tgWebAppPlatform),
          theme: isTelegramDark() ? 'dark' : 'light',
        });
      } catch {}
    } else if (!isAuthenticated) {
      clearTelemetryUser();
    }
  }, [isAuthenticated, tgUser]);

  const value = useMemo(
    () => ({ isAuthenticated, authError, authenticate, rawInitData }),
    [isAuthenticated, authError, authenticate, rawInitData]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
